import {animate, useReducedMotion} from 'framer-motion';

import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {useOnMount} from 'shared/web';

import {getRouteAnimationDuration} from '../utils/getRouteAnimationDuration';

// An even pace that slows down only at the finish, as cubic-bezier control points.
const ROUTE_EASING: [number, number, number, number] = [0.3, 0.55, 0.45, 1];

/**
 * Draws the route over its distance (like a Strava flyby) every time `start` is called - when the
 * map first comes into view and on replay. With reduced motion the whole route is shown at once
 * and there is nothing to replay.
 */
export function useRouteAnimation(totalDistance: number) {
  const shouldReduceMotion = useReducedMotion() ?? false;
  // Null until the drawing starts, so nothing is drawn before the map is in view.
  const [animatedDistance, setAnimatedDistance] = useState<number | null>(null);
  const controlsRef = useRef<ReturnType<typeof animate> | null>(null);

  useOnMount(undefined, () => controlsRef.current?.stop());

  const start = () => {
    if (shouldReduceMotion) {
      return;
    }
    controlsRef.current?.stop();
    controlsRef.current = animate(0, totalDistance, {
      duration: getRouteAnimationDuration(totalDistance),
      ease: ROUTE_EASING,
      onUpdate: setAnimatedDistance,
    });
  };

  const drawnDistance = shouldReduceMotion ? totalDistance : animatedDistance;

  return {
    drawnDistance,
    isFinished: isNotNil(drawnDistance) && drawnDistance >= totalDistance,
    shouldReduceMotion,
    canReplay: !shouldReduceMotion && totalDistance > 0,
    start,
  };
}
